/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Context Hub overview", description = "Configure the business knowledge, brand guidance, assets, and connections that agents use across Hightouch.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Context Hub overview",
        description: "Configure the business knowledge, brand guidance, assets, and connections that agents use across Hightouch.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Where Context Hub is used",
          "depth": 0,
          "id": "where-context-hub-is-used"
        }, {
          "value": "What you can configure",
          "depth": 0,
          "id": "what-you-can-configure"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "Manage Context Hub across your team",
          "depth": 0,
          "id": "manage-context-hub-across-your-team"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins, data teams, marketing teams, and creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsx("li", {
                  children: "At least one agent product enabled: Agents, Ad Studio, or Lifecycle Studio."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Configure shared business knowledge, brand guidance, assets, and connections that agents can use across Hightouch."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Context Hub gives agents information about your business that they can reuse across tasks. Instead of repeating the same definitions, rules, and creative guidelines in every prompt, configure them in one place."
      }), "\n", _jsx(_components.p, {
        children: "For example, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Define business terms such as ", _jsx(_components.em, {
            children: "active customer"
          }), " or ", _jsx(_components.em, {
            children: "high-value account"
          }), ", and approve definitions agents propose while they work."]
        }), "\n", _jsx(_components.li, {
          children: "Add brand logos, fonts, product catalogs, and other creative assets."
        }), "\n", _jsx(_components.li, {
          children: "Set guidelines for generated images, ads, and emails, and save approved copy as references."
        }), "\n", _jsx(_components.li, {
          children: "Connect advertising platforms, email providers, and digital asset managers."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Context Hub doesn't replace your data sources or the instructions you give an agent for a specific task. Configure data access through your sources, models, and permissions. Use Context Hub for the business knowledge and guidance agents need when working with that data."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "where-context-hub-is-used",
        children: "Where Context Hub is used"
      }), "\n", _jsx(_components.p, {
        children: "Context Hub provides shared information to Agents, Ad Studio, and Lifecycle Studio. Each product uses the information relevant to its workflows."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Product"
            }), _jsx(_components.th, {
              children: "Uses"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/agents/overview",
                children: "Agents"
              })
            }), _jsx(_components.td, {
              children: "Business context, rules, skills, and definitions"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ad-studio/overview",
                children: "Ad Studio"
              })
            }), _jsx(_components.td, {
              children: "Business context, skills, definitions, brand profiles, creative guidance, assets, guardrails, and advertising-platform connections"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/lifecycle-studio/overview",
                children: "Lifecycle Studio"
              })
            }), _jsx(_components.td, {
              children: "Business context, skills, definitions, brand guidance, copy references, styles, email guardrails, and email service provider connections"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You can limit each file, skill, definition, and style to the products that need it. See ", _jsx(_components.a, {
          href: "/context-hub/business-context#choose-where-agents-use-context",
          children: "Choose where agents use context"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The audience builder in ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " uses your\n", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "Customer Studio schema"
          }), ", not Context Hub. To change\nhow it builds audiences, update the models, relationships, traits, or events\nin your schema."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-you-can-configure",
        children: "What you can configure"
      }), "\n", _jsxs(_components.p, {
        children: ["Context Hub is organized into ", _jsx(_components.strong, {
          children: "Brands"
        }), ", ", _jsx(_components.strong, {
          children: "Knowledge"
        }), ", ", _jsx(_components.strong, {
          children: "Guardrails"
        }), ", ", _jsx(_components.strong, {
          children: "Assets"
        }), ", and ", _jsx(_components.strong, {
          children: "Connected data"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If you want to…"
            }), _jsx(_components.th, {
              children: "Start here"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Define brand identity, including logos, colors, and fonts"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/brands",
                children: "Brands"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add business definitions, rules, and reusable instructions"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/context-hub/business-context",
                children: "Business context"
              }), " in ", _jsx(_components.strong, {
                children: "Knowledge"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Guide the appearance and wording of generated content"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/creative",
                children: "Styles and copy references"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Control how agents generate, modify, and review creative content"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/guardrails",
                children: "Guardrails"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Provide images, templates, videos, and product catalogs"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/assets",
                children: "Assets"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Connect advertising, lifecycle, and creative platforms, or configure personalization data"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/connections",
                children: "Connected data"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync metric and dimension definitions from Snowflake or Databricks"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/external",
                children: "External context sources"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Guardrails"
        }), " and ", _jsx(_components.strong, {
          children: "Assets"
        }), " appear only when Ad Studio or Lifecycle Studio is enabled. You can also open the Context Agent chat panel to add, update, or review context through a conversation."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "You don't need to configure every part of Context Hub before using agents. Start with one task, then add context as needed."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If you want to…"
            }), _jsx(_components.th, {
              children: "Start by…"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Analyze last month's paid campaign performance with ", _jsx(_components.a, {
                href: "/agents/chats",
                children: "Agents"
              })]
            }), _jsxs(_components.td, {
              children: ["Connecting your advertising platforms in ", _jsx(_components.a, {
                href: "/context-hub/connections#channels",
                children: "Connected data"
              }), " and adding a ", _jsx(_components.a, {
                href: "/context-hub/business-context#add-business-context",
                children: "business context file"
              }), " that explains how you calculate metrics such as ROAS"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Generate ads for a product launch in ", _jsx(_components.a, {
                href: "/ad-studio/ads",
                children: "Ad Studio"
              })]
            }), _jsxs(_components.td, {
              children: ["Configuring your ", _jsx(_components.a, {
                href: "/context-hub/brands",
                children: "brand kit"
              }), ", adding product photos and a ", _jsx(_components.a, {
                href: "/context-hub/assets#connect-a-product-catalog",
                children: "product catalog"
              }), ", and setting ", _jsx(_components.a, {
                href: "/context-hub/guardrails#limit-changes-for-specific-asset-collections",
                children: "asset modification restrictions"
              }), " for licensed imagery"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Create an email campaign in ", _jsx(_components.a, {
                href: "/lifecycle-studio/campaigns",
                children: "Lifecycle Studio"
              })]
            }), _jsxs(_components.td, {
              children: ["Configuring your ", _jsx(_components.a, {
                href: "/context-hub/brands",
                children: "brand kit"
              }), ", approving ", _jsx(_components.a, {
                href: "/context-hub/creative#add-copy-references",
                children: "copy references"
              }), ", and ", _jsx(_components.a, {
                href: "/context-hub/connections#sync-personalization-fields-and-content-blocks",
                children: "syncing personalization fields"
              }), " from your email service provider"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "After configuring the relevant context, run the task and review the result. If an agent uses an incorrect business definition or misses important guidance, update the corresponding context and try again."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-context-hub-across-your-team",
        children: "Manage Context Hub across your team"
      }), "\n", _jsx(_components.p, {
        children: "Different teams can maintain the information they know best:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Team"
            }), _jsx(_components.th, {
              children: "Typically manages"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Platform admins"
            }), _jsx(_components.td, {
              children: "Permissions and workspace configuration"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Data teams"
            }), _jsx(_components.td, {
              children: "Business definitions, metrics, rules, and data connections"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Marketing and creative teams"
            }), _jsx(_components.td, {
              children: "Brand profiles, creative assets, styles, and content guidelines"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Anyone who configures Context Hub needs the ", _jsxs(_components.a, {
          href: "/workspace-management/roles#agents",
          children: [_jsx(_components.strong, {
            children: "Configure Agents"
          }), " grant"]
        }), ". Without it, Context Hub doesn't appear in the navigation. See ", _jsx(_components.a, {
          href: "/workspace-management/roles#agents",
          children: "Roles"
        }), " for permission details and ", _jsx(_components.a, {
          href: "/agents/overview#data-governance",
          children: "Agents data governance"
        }), " for how data permissions affect agents."]
      }), "\n", _jsxs(_components.p, {
        children: ["Assign owners for shared context and review it when your business definitions, brand guidelines, or connected platforms change. To find outdated or conflicting definitions, ", _jsx(_components.a, {
          href: "/context-hub/business-context#run-a-context-review",
          children: "run a context review"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
